import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";
import "./styles/CalendarPage.css";

/* Colours mapped to the project's design tokens (variables.css) */
const CATEGORY_COLORS = {
  "Health Camp": "var(--bc-primary-d)",      /* brand green */
  Workshop: "var(--color-indigo)",            /* indigo */
  Wellness: "var(--color-warning)",           /* amber */
  Talk: "var(--color-secondary)",             /* teal */
  Appointment: "var(--color-danger-rose)",    /* rose */
  default: "var(--color-text-slate-500)",     /* slate */
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/* ── Date helpers (local, no timezone surprises) ── */
const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -d.getDay());

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};
const timeRange = (s, e) => (e ? `${fmt12(s)} – ${fmt12(e)}` : fmt12(s));
const colorOf = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.default;

const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6 AM → 9 PM

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // month | week | day
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    setLoading(true);
    // Public feed: only Active camps, served by CampController::publicList
    fetch(`${API_BASE_URL}api/camps`, {
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const list = data && data.status && Array.isArray(data.data) ? data.data : [];
        setEvents(list);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [token]);

  /* Group events by date key for quick lookup */
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return map;
  }, [events]);

  const eventsOn = (d) => eventsByDate[toKey(d)] || [];

  /* ── Navigation ── */
  const go = (dir) => {
    if (view === "month") {
      const x = new Date(cursor);
      x.setMonth(x.getMonth() + dir);
      setCursor(x);
    } else if (view === "week") {
      setCursor(addDays(cursor, dir * 7));
    } else {
      setCursor(addDays(cursor, dir));
    }
  };
  const goToday = () => setCursor(new Date());

  /* ── Title ── */
  const title = useMemo(() => {
    if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === "week") {
      const s = startOfWeek(cursor);
      const e = addDays(s, 6);
      const sM = MONTHS[s.getMonth()].slice(0, 3);
      const eM = MONTHS[e.getMonth()].slice(0, 3);
      return `${sM} ${s.getDate()} – ${eM} ${e.getDate()}, ${e.getFullYear()}`;
    }
    return cursor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [view, cursor]);

  /* ── Month grid cells ── */
  const monthCells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = startOfWeek(first);
    const cells = [];
    for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
    // trim trailing empty week if it spills entirely into next month
    return cells.slice(0, cells[35] && cells[35].getMonth() !== cursor.getMonth() && cells[35].getDate() > 7 ? 35 : 42);
  }, [cursor]);

  const weekDays = useMemo(() => {
    const s = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [cursor]);

  const today = new Date();

  /* Event chip click */
  const openEvent = (e) => setSelected(e);

  /* ── Renderers ── */
  const renderMonth = () => (
    <div className="calx-month">
      <div className="calx-weekrow calx-weekhead">
        {WEEKDAYS.map((w) => (
          <div className="calx-weekhead-cell" key={w}>{w}</div>
        ))}
      </div>
      <div className="calx-monthgrid">
        {monthCells.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const dayEvents = eventsOn(d);
          return (
            <div className={`calx-cell ${inMonth ? "" : "is-other"} ${isToday ? "is-today" : ""}`} key={i}>
              <div className="calx-cell-num">{d.getDate()}</div>
              <div className="calx-cell-events">
                {dayEvents.slice(0, 3).map((e) => (
                  <button
                    type="button"
                    className="calx-chip"
                    style={{ background: colorOf(e.category) }}
                    key={e.id}
                    onClick={() => openEvent(e)}
                    title={e.title}
                  >
                    <span className="calx-chip-time">{fmt12(e.start_time)}</span> {e.title}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <button
                    type="button"
                    className="calx-more"
                    onClick={() => { setCursor(new Date(d)); setView("day"); }}
                  >
                    +{dayEvents.length - 3} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderTimeGrid = (days) => (
    <div className="calx-time">
      <div className="calx-time-head" style={{ gridTemplateColumns: `64px repeat(${days.length}, 1fr)` }}>
        <div className="calx-time-corner" />
        {days.map((d, i) => {
          const isToday = sameDay(d, today);
          return (
            <div className={`calx-time-dayhead ${isToday ? "is-today" : ""}`} key={i}>
              <span className="calx-time-dow">{WEEKDAYS[d.getDay()]}</span>
              <span className="calx-time-date">{d.getDate()}</span>
            </div>
          );
        })}
      </div>

      <div className="calx-time-body" style={{ gridTemplateColumns: `64px repeat(${days.length}, 1fr)` }}>
        <div className="calx-time-axis">
          {HOURS.map((h) => (
            <div className="calx-time-slot-label" key={h}>{fmt12(`${String(h).padStart(2, "0")}:00`)}</div>
          ))}
        </div>

        {days.map((d, di) => {
          const dayEvents = eventsOn(d);
          return (
            <div className="calx-time-col" key={di}>
              {HOURS.map((h) => <div className="calx-time-slot" key={h} />)}
              {dayEvents.map((e) => {
                const [sh, sm] = (e.start_time || "06:00").split(":").map(Number);
                const [eh, em] = (e.end_time || e.start_time || "07:00").split(":").map(Number);
                const top = ((sh + sm / 60) - 6) * 48;
                const height = Math.max(24, ((eh + em / 60) - (sh + sm / 60)) * 48);
                if (sh < 6 || sh > 21) return null;
                return (
                  <button
                    type="button"
                    className="calx-time-event"
                    key={e.id}
                    style={{ top: `${top}px`, height: `${height}px`, background: colorOf(e.category) }}
                    onClick={() => openEvent(e)}
                  >
                    <span className="calx-time-event-title">{e.title}</span>
                    <span className="calx-time-event-time">{timeRange(e.start_time, e.end_time)}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <Header />
  <section className="health-calendar-banner">
        <div className="container">
       

          <div className="health-calendar-banner-content">
            <div className="health-calendar-icon">
              <i className="fa-solid fa-calendar-days"></i>
            </div>

            <div>
              {/* <span className="health-calendar-tag">HEALTH & WELLNESS</span> */}
              <h1>Camp 2026</h1>
              <p>
                Best Camp Days
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Breadcrumb */}
            <section className="cl-breadcrumb-bar py-xl-3 py-3">
              <div className="container-fluid">
                <nav aria-label="breadcrumb">
                 <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to="/">
                        <i className="fa-solid fa-house me-1"></i> Home
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Camp
                    </li>
                  </ol>
                </nav>
              </div>
            </section>
      <main className="calx-page">
        <div className="container">
          {/* Breadcrumb */}
          {/* Header row */}
          <div className="calx-head">
            <div>
              <span className="calx-tag">CAMPS & SCHEDULE</span>
              <h1>Calendar</h1>
              <p>All camps, workshops, and sessions at a glance — switch between month, week, and day.</p>
            </div>
            <div className="calx-legend">
              {Object.entries(CATEGORY_COLORS).filter(([k]) => k !== "default").map(([label, color]) => (
                <span className="calx-legend-item" key={label}>
                  <span className="calx-legend-dot" style={{ background: color }} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Toolbar */}
          <div className="calx-toolbar">
            <div className="calx-nav">
              <button type="button" className="calx-nav-btn" onClick={() => go(-1)} aria-label="Previous">
                <i className="fa-solid fa-chevron-left" />
              </button>
              <button type="button" className="calx-nav-btn" onClick={() => go(1)} aria-label="Next">
                <i className="fa-solid fa-chevron-right" />
              </button>
              <button type="button" className="calx-today" onClick={goToday}>Today</button>
            </div>

            <h2 className="calx-period">{title}</h2>

            <div className="calx-views">
              {["month", "week", "day"].map((v) => (
                <button
                  type="button"
                  key={v}
                  className={`calx-view-btn ${view === v ? "is-active" : ""}`}
                  onClick={() => setView(v)}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar body */}
          <div className="calx-card">
            {loading && (
              <div className="calx-loading">
                <div className="calx-spinner" />
              </div>
            )}
            {!loading && events.length === 0 && (
              <div className="calx-empty" style={{ padding: "48px 20px", textAlign: "center" }}>
                <p>No upcoming camps right now — check back soon.</p>
              </div>
            )}
            {view === "month" && renderMonth()}
            {view === "week" && renderTimeGrid(weekDays)}
            {view === "day" && renderTimeGrid([cursor])}
          </div>
        </div>
      </main>

      {/* Event detail popup */}
      {selected && (
        <div className="calx-modal-bg" onClick={() => setSelected(null)}>
          <div className="calx-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="calx-modal-close" onClick={() => setSelected(null)} aria-label="Close">
              <i className="fa-solid fa-xmark" />
            </button>

            <span className="calx-modal-tag" style={{ background: colorOf(selected.category) }}>
              {selected.category || "Camp"}
            </span>

            <h3 className="calx-modal-title">{selected.title}</h3>

            {selected.description && (
              <p className="calx-modal-desc">{selected.description}</p>
            )}

            <div className="calx-modal-rows">
              <div className="calx-modal-row">
                <i className="fa-solid fa-calendar-day" />
                {new Date(`${selected.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
              <div className="calx-modal-row">
                <i className="fa-solid fa-clock" />
                {timeRange(selected.start_time, selected.end_time)}
              </div>
              {selected.location && (
                <div className="calx-modal-row">
                  <i className="fa-solid fa-location-dot" />
                  {selected.location}
                </div>
              )}
              {selected.speaker && (
                <div className="calx-modal-row">
                  <i className="fa-solid fa-user" />
                  {selected.speaker}
                </div>
              )}
            </div>

            <Link to="/contact" className="calx-modal-cta">
              Book / Enquire
              <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default CalendarPage;